import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { registerSchema, type RegisterInput, type LoginResponse } from '@octery/shared';
import { api, ApiClientError } from '@/lib/api';
import { useAuthStore } from '@/stores/auth.store';
import { navigate } from '@/app/navigation';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Link } from '@/app/router';

export function RegisterPage() {
  const searchParams = new URLSearchParams(window.location.search);
  const inviteToken = searchParams.get('invite') || undefined;
  const invitedEmail = searchParams.get('email') || '';
  const isInviteFlow = Boolean(inviteToken);

  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const setAuth = useAuthStore((s) => s.setAuth);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<RegisterInput>({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      email: invitedEmail || undefined,
      inviteToken,
    },
  });

  const onSubmit = async (data: RegisterInput) => {
    setError(null);
    setIsLoading(true);
    try {
      const response = await api.post<LoginResponse>('/api/auth/register', {
        ...data,
        inviteToken,
      });
      setAuth(response.user);
      navigate('/');
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Registration failed');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-muted/30 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl">Create account</CardTitle>
          <CardDescription>
            {isInviteFlow ? 'Create your account to join the invited company' : 'Get started with Octery'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            {error && (
              <div className="bg-destructive/10 text-destructive text-sm p-3 rounded-md">
                {error}
              </div>
            )}

            <div className="space-y-2">
              <label htmlFor="name" className="text-sm font-medium">Name</label>
              <Input id="name" placeholder="John Doe" {...register('name')} />
              {errors.name && <p className="text-destructive text-xs">{errors.name.message}</p>}
            </div>

            <div className="space-y-2">
              <label htmlFor="email" className="text-sm font-medium">Email</label>
              <Input id="email" type="email" placeholder="you@company.com" {...register('email')} />
              {errors.email && <p className="text-destructive text-xs">{errors.email.message}</p>}
            </div>

            {!isInviteFlow && (
              <div className="space-y-2">
                <label htmlFor="companyName" className="text-sm font-medium">Company name</label>
                <Input id="companyName" placeholder="Acme Inc." {...register('companyName')} />
                {errors.companyName && <p className="text-destructive text-xs">{errors.companyName.message}</p>}
              </div>
            )}

            <div className="space-y-2">
              <label htmlFor="password" className="text-sm font-medium">Password</label>
              <Input id="password" type="password" placeholder="••••••••" {...register('password')} />
              {errors.password && <p className="text-destructive text-xs">{errors.password.message}</p>}
            </div>

            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading ? 'Creating account...' : 'Create account'}
            </Button>

            <p className="text-center text-sm text-muted-foreground">
              Already have an account?{' '}
              <Link href="/login" className="text-primary hover:underline">Sign in</Link>
            </p>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
